import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Phone, Mail } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const FORM_NAME = 'membership-inquiry';

const ContactSection = () => {
const gymLocationUrl =
'https://www.google.com/maps/search/?api=1&query=8WVJ%2BC6%20Gaborone';

const [searchParams] = useSearchParams();
const selectedPlanFromUrl = searchParams.get('plan') || '';

const [formData, setFormData] = useState({
fullName: '',
email: '',
phone: '',
program: '',
selectedPlan: selectedPlanFromUrl,
message: ''
});

const [isSubmitting, setIsSubmitting] = useState(false);
const [submitStatus, setSubmitStatus] = useState('idle');

useEffect(() => {
setFormData(prev => ({
...prev,
selectedPlan: selectedPlanFromUrl
}));
}, [selectedPlanFromUrl]);

const handleSubmit = async (e) => {
e.preventDefault();

setIsSubmitting(true);
setSubmitStatus('idle');

try {
  const payload = new URLSearchParams({
    'form-name': FORM_NAME,
    fullName: formData.fullName,
    email: formData.email,
    phone: formData.phone,
    program: formData.program,
    selectedPlan: formData.selectedPlan,
    message: formData.message,
    'bot-field': ''
  });

  const response = await fetch('/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: payload.toString()
  });

  if (!response.ok) {
    throw new Error('Form submission failed');
  }

  setSubmitStatus('success');

  setFormData({
    fullName: '',
    email: '',
    phone: '',
    program: '',
    selectedPlan: selectedPlanFromUrl,
    message: ''
  });

} catch (error) {
  setSubmitStatus('error');

} finally {
  setIsSubmitting(false);
}

};

const handleChange = (e) => {
setFormData({
...formData,
[e.target.name]: e.target.value
});
};

return (
<section id="contact" className="bg-black py-20 md:py-32" aria-labelledby="contact-heading" >

  <div className="container mx-auto px-6">

    {/* Section Header */}
    <div className="text-center mb-16">

      <div className="text-red-500 text-sm font-semibold mb-4 tracking-widest">
        TRANSFORM TODAY
      </div>

      <h2
        id="contact-heading"
        className="text-white text-5xl md:text-6xl font-bold mb-6"
      >
        Join Bond Boxing in Gaborone
      </h2>

      <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto">
        Ready to start boxing or improve your fitness?
        Get in touch with Bond Boxing Gym at Notwane Club,
        Gaborone, Botswana. Whether you're a beginner,
        fitness enthusiast or aspiring competitive boxer,
        take the first step towards your training journey.
      </p>

    </div>

    <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">

      {/* Business Information */}
      <div className="space-y-8">

        {/* Location */}
        <div className="bg-zinc-950 border border-gray-800 p-8 hover:border-red-600 transition-colors duration-300">

          <div className="flex items-start gap-4">

            <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0">
              <MapPin className="text-white" size={24} aria-hidden="true" />
            </div>

            <div>
              <h3 className="text-white font-bold text-xl mb-2">
                Boxing Gym Location
              </h3>

              <a
                href={gymLocationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-col gap-1 text-gray-400 transition-colors duration-300 hover:text-red-500"
                aria-label="Open Bond Boxing Gym at Notwane Club in Gaborone on Google Maps"
              >
                <span>Bond Boxing Gym</span>
                <span>Notwane Club, Gaborone, Botswana</span>
                <span className="text-sm uppercase tracking-wider text-red-500">
                  Open in Google Maps
                </span>
              </a>

            </div>
          </div>
        </div>

        {/* Training Hours */}
        <div className="bg-zinc-950 border border-gray-800 p-8 hover:border-red-600 transition-colors duration-300">

          <div className="flex items-start gap-4">

            <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0">
              <Clock className="text-white" size={24} aria-hidden="true" />
            </div>

            <div>

              <h3 className="text-white font-bold text-xl mb-2">
                Boxing Training Hours
              </h3>

              <div className="space-y-1 text-gray-400">

                <p className="font-semibold">Monday - Friday</p>
                <p className="ml-4">Morning: 5:00 AM - 9:00 AM</p>
                <p className="ml-4">Afternoon: 3:30 PM - 7:30 PM</p>

                <p className="mt-3 font-semibold">Saturday</p>
                <p className="ml-4">7:00 AM - 9:00 AM</p>

                <p className="mt-3 font-semibold">Sunday</p>
                <p className="ml-4">Closed</p>

                <div className="mt-4 space-y-2 border-t border-gray-800 pt-4">
                  <p>Coach-led training sessions</p>
                  <p>Suitable for beginners to advanced levels</p>
                  <p>No prior boxing experience required</p>
                  <p>45-60 minute sessions</p>
                </div>

              </div>

            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-zinc-950 border border-gray-800 p-8 hover:border-red-600 transition-colors duration-300">

          <div className="flex items-start gap-4">

            <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0">
              <Phone className="text-white" size={24} aria-hidden="true" />
            </div>

            <div className="w-full">

              <h3 className="text-white font-bold text-xl mb-3">
                Contact Bond Boxing
              </h3>

              <div className="space-y-2">

                <a
                  href="tel:+26771663001"
                  className="text-gray-400 hover:text-red-500 transition-colors duration-300 flex items-center gap-2"
                >
                  <Phone size={16} aria-hidden="true" />
                  +267 71 663 001
                </a>

                <a
                  href="mailto:info@bondboxing.com"
                  className="text-gray-400 hover:text-red-500 transition-colors duration-300 flex items-center gap-2"
                >
                  <Mail size={16} aria-hidden="true" />
                  info@bondboxing.com
                </a>

              </div>

            </div>
          </div>
        </div>

      </div>

      {/* Membership Inquiry Form */}
      <div className="bg-zinc-950 border border-gray-800 p-8">

        <h3 className="text-white font-bold text-2xl mb-6">
          Boxing Gym Membership Inquiry
        </h3>

        <p className="text-gray-400 mb-6">
          Interested in joining Bond Boxing? Complete the
          form and a coach will reach out within 24 hours.
        </p>

        <form
          name={FORM_NAME}
          data-netlify="true"
          netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="space-y-6"
        >

          <input type="hidden" name="form-name" value={FORM_NAME} />
          <input type="hidden" name="bot-field" value="" />

          <div>
            <label htmlFor="fullName" className="text-white font-medium mb-2 block">
              Full Name
            </label>

            <input
              id="fullName"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              autoComplete="name"
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors duration-300"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="text-white font-medium mb-2 block">
              Email Address
            </label>

            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors duration-300"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="text-white font-medium mb-2 block">
              Phone Number (Optional)
            </label>

            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors duration-300"
              placeholder="+267 71 663 001"
            />
          </div>

          {formData.selectedPlan && (
            <div>
              <label htmlFor="selectedPlan" className="text-white font-medium mb-2 block">
                Selected Membership Plan
              </label>

              <input
                id="selectedPlan"
                type="text"
                name="selectedPlan"
                value={formData.selectedPlan}
                readOnly
                className="w-full bg-black border border-gray-700 text-red-500 px-4 py-3 focus:outline-none cursor-not-allowed"
              />
            </div>
          )}

          <div>
            <label htmlFor="program" className="text-white font-medium mb-2 block">
              I'm Interested In
            </label>

            <select
              id="program"
              name="program"
              value={formData.program}
              onChange={handleChange}
              required
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors duration-300"
            >
              <option value="">Select a program</option>
              <option value="youth">Youth Development Program</option>
              <option value="conditioning">Elite Classes</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="text-white font-medium mb-2 block">
              Additional Details (Optional)
            </label>

            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full bg-black border border-gray-700 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors duration-300 resize-none"
              placeholder="Tell us about your goals and training experience..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-semibold tracking-wide transition-all duration-300 border border-red-600 hover:border-red-700 hover:shadow-lg hover:shadow-red-600/50"
          >
            {isSubmitting ? 'Submitting...' : 'Secure Your Spot'}
          </button>

          {submitStatus === 'success' && (
            <p
              role="status"
              className="border border-green-700 bg-green-950/40 px-4 py-3 text-sm text-green-300"
            >
              Your inquiry was sent successfully. A coach will contact you within 24 hours.
            </p>
          )}

          {submitStatus === 'error' && (
            <p
              role="alert"
              className="border border-red-700 bg-red-950/40 px-4 py-3 text-sm text-red-300"
            >
              We could not send your inquiry. Please try again or email info@bondboxing.com directly.
            </p>
          )}

        </form>

      </div>

    </div>

  </div>

</section>

);
};

export default ContactSection;